<template>
    <v-container fill-height fluid ma-0 pa-0 ref="content_renderer_container" v-resize="setBreakpoint">
        <v-row class="fill-height" justify="center" align-content="start" :no-gutters="content.padding != 1">
            <v-col
                    v-for="contentElement of content.contentelements"
                    :key="contentElement.contentelementid"
                    :cols="resolveCols(contentElement)"
            >
                <v-card-title
                        v-if="!!contentElement.title_textassetid"
                        v-html="$ta(contentElement.title_textassetid)"
                ></v-card-title>
                <v-card-text
                        v-if="!!contentElement.content_textassetid"
                        v-html="$ta(contentElement.content_textassetid)"
                        :style="{'text-align': contentElement.align}"
                ></v-card-text>
                <v-row v-if="shouldRender && !!contentElement.mediacomposition" justify="center">
                    <MediaCompositionFramework
                            v-if="frameworkControls"
                            :media-composition="contentElement.mediacomposition"
                            :subtitles-default="contentElement.subtitles_default"
                            :autoplay="true"
                            :max-height="getMaxHeight(contentElement.size)"
                            :delay="mediaDelay"
                    ></MediaCompositionFramework>
                    <MediaComposition
                            v-else
                            :ref="'mediacompostion_' + contentElement.mediacomposition.mediacompositionid"
                            :media-composition="contentElement.mediacomposition"
                            :subtitles-default="contentElement.subtitles_default"
                            :max-height="getMaxHeight(contentElement.size)"
                            :delay="mediaDelay"
                    ></MediaComposition>
                </v-row>
                <v-row
                        v-else-if="shouldRender && !!contentElement.contentgraphid"
                        justify="center"
                >
                    <v-lazy transition="scale-transition">
                        <transition name="scale-transition">
                            <ContentGraphRenderer
                                    v-if="numCharts < 3 || !!chartState[contentElement.contentelementid]"
                                    :data="contentElement.contentgraph"
                                    :max-height="getMaxHeight(contentElement.size)"></ContentGraphRenderer>
                        </transition>
                    </v-lazy>
                </v-row>
                <v-row
                        v-else-if="!!contentElement.image_assetid"
                        :style="{'max-height': getMaxHeight(contentElement.size)}"
                        justify="center"
                >
                    <img
                            :src="$asset(contentElement.image_assetid)"
                            :style="{'max-height': !!contentElement.image_height ? (contentElement.image_height + 'px') : '100%', 'max-width': !!contentElement.image_width ? (contentElement.image_width + 'px') : '100%'}"
                            width="100%"
                            class="nodrag"
                    >
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import MediaCompositionFramework from '../mediacomposition/MediaCompositionFramework'
    import MediaComposition from "../mediacomposition/MediaComposition";
    import ContentGraphRenderer from "./ContentGraph/ContentGraphRenderer";

    export default {
        props: {
            mediaDelay: {
                default: null,
            },
            content: {
                required: true,
                type: Object
            },
            mini: {
                type: Boolean,
                default: false
            },
            hidden: {
                type: Boolean,
                default: false
            },
            defaultSize: {
                type: String,
                default: 'small'
            },
            frameworkControls: {
                default: true,
                type: Boolean
            }
        },
        components: {
            MediaCompositionFramework,
            ContentGraphRenderer,
            MediaComposition
        },
        data: () => ({
            breakpoint: 'md',
            maxHeights: {
                small: {
                    xs: '10vh',
                    md: '15vh',
                    lg: '20vh',
                },
                medium: {
                    xs: '15vh',
                    md: '30vh',
                    lg: '40vh',
                },
                large: {
                    xs: '20vh',
                    md: '40vh',
                    lg: '60vh',
                }
            },
            shouldRender: false,
            chartState: {}
        }),
        computed: {
            numCharts() {
                return this.content.contentelements.filter(({contentgraphid}) => !!contentgraphid).length;
            }
        },
        watch: {
            hidden(val) {
                !val && (() => {
                    this.shouldRender = true;
                    this.$nextTick(() => {
                        this.delayCharts();
                    });
                })();
            },
        },
        methods: {
            pauseAllMedia() {
                let ref;
                this.content.contentelements.forEach((contentElement) => {
                    if (contentElement.mediacompositionid) {
                        ref = this.$refs['mediacompostion_' + contentElement.mediacompositionid];
                        ref && !!ref[0] && ref[0].pause();
                    }
                });
            },
            setBreakpoint() {
                if (this.mini) {
                    let width = parseFloat(getComputedStyle(this.$refs.content_renderer_container).width.replace('px', ''));
                    if (width < 960) {
                        this.breakpoint = 'xs';
                    } else if (width >= 960 && width < 1264) {
                        this.breakpoint = 'md';
                    } else if (width >= 1264) {
                        this.breakpoint = 'lg';
                    }
                } else {
                    this.breakpoint = (() => {
                        switch (this.$vuetify.breakpoint.name) {
                            case 'xs':
                            case 'sm':
                                return 'xs';
                            case 'md':
                                return 'md';
                            case 'lg':
                            case 'xl':
                                return 'lg';
                        }
                    })();
                }
            },
            resolveCols(contentElement, breakpoint = null) {
                if (!breakpoint) {
                    breakpoint = this.breakpoint;
                }
                switch (breakpoint) {
                    case 'lg':
                        return contentElement[breakpoint] || this.resolveCols(contentElement, 'md');
                    case 'md':
                        return contentElement[breakpoint] || this.resolveCols(contentElement, 'xs');
                    case 'xs':
                        return contentElement[breakpoint] || 12;//Default to 12 columns if current or smaller breakpoint is not specified.
                }
            },
            getMaxHeight(size) {
                return this.maxHeights[size || this.defaultSize][this.breakpoint];
            },
            delayCharts() {
                let i = 1;
                this.content.contentelements.forEach(el => {
                    if (el.contentgraphid) {
                        setTimeout(() => {
                            this.$set(this.chartState, el.contentelementid, true);
                        }, this.numCharts > 2 ? (500 * i) : 0);
                        i++;
                    }
                });
            }
        },
        mounted() {
            this.shouldRender = !this.hidden;
            if (this.shouldRender) {
                this.delayCharts();
            }
        }
    };
</script>
<style>
    .nodrag {
        user-drag: none;
        user-select: none;
        -moz-user-select: none;
        -webkit-user-drag: none;
        -webkit-user-select: none;
        -ms-user-select: none;
    }

</style>